import {
  formatSessionsByCountry,
  formatSessionsByMonth,
  formatSessionsByScore,
  fulfilledState,
  getDefaultDates,
  pendingState,
  rejectedState,
  resError,
} from "@helpers";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import DashboardService from "@services/DashboardService";

export const getSessionsByMonth = createAsyncThunk(
  "dashboard/getSessionsByMonth",
  async (params, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getSessionsByMonth(params);

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const getSessionsByScore = createAsyncThunk(
  "dashboard/getSessionsByScore",
  async (params, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getSessionsByScore(params);

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const getSessionsByCountry = createAsyncThunk(
  "dashboard/getSessionsByCountry",
  async (params, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getSessionsByCountry(params);

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const getLastActivity = createAsyncThunk(
  "dashboard/getLastActivity",
  async (_, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getLastActivity();

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const getUsersRanking = createAsyncThunk(
  "dashboard/getUsersRanking",
  async (params, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getUsersRanking(params);

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const fetchAllDashboardData = createAsyncThunk(
  "dashboard/fetchAllDashboardData",
  async (params, {dispatch, rejectWithValue}) => {
    const requests = [
      {
        type: "sessionsByMonth",
        thunk: getSessionsByMonth,
        thunkParams: params,
      },
      {
        type: "sessionsByScore",
        thunk: getSessionsByScore,
        thunkParams: params,
      },
      {
        type: "sessionsByCountry",
        thunk: getSessionsByCountry,
        thunkParams: params,
      },
      {
        type: "lastActivity",
        thunk: getLastActivity,
        thunkParams: undefined,
      },
      {
        type: "usersRanking",
        thunk: getUsersRanking,
        thunkParams: params,
      },
    ];

    const results = [];

    for (const req of requests) {
      try {
        const res = await dispatch(
          req.thunkParams !== undefined
            ? req.thunk(req.thunkParams)
            : req.thunk(),
        );

        if (res.error) {
          return rejectWithValue(resError(res.error));
        }

        if (res.payload) {
          results.push({type: req.type, data: res.payload, error: null});
        } else {
          results.push({type: req.type, data: null, error: null});
        }
      } catch (err) {
        return rejectWithValue(resError(err));
      }
    }

    return results;
  },
);

export const fetchSessionsChartsData = createAsyncThunk(
  "dashboard/fetchSessionsChartsData",
  async (params, {dispatch, rejectWithValue}) => {
    const requests = [
      {
        type: "sessionsByMonth",
        thunk: getSessionsByMonth,
        thunkParams: params,
      },
      {
        type: "sessionsByScore",
        thunk: getSessionsByScore,
        thunkParams: params,
      },
    ];

    const results = [];

    for (const req of requests) {
      try {
        const res = await dispatch(req.thunk(req.thunkParams));

        if (res.error) {
          return rejectWithValue(resError(res.error));
        }

        if (res.payload) {
          results.push({type: req.type, data: res.payload, error: null});
        } else {
          results.push({type: req.type, data: null, error: null});
        }
      } catch (err) {
        return rejectWithValue(resError(err));
      }
    }

    return results;
  },
);

const initialState = {
  sessions: {
    data: {
      month: [],
      score: [],
    },
    date: {
      from: getDefaultDates().from,
      to: getDefaultDates().to,
    },
    errors: null,
    error: "",
    status: "",
  },
  map: {
    data: [],
    date: {
      from: getDefaultDates().from,
      to: getDefaultDates().to,
    },
    errors: null,
    error: "",
    status: "",
  },
  lastActivity: {
    data: [],
    errors: null,
    error: "",
    status: "",
  },
  usersRanking: {
    data: [],
    page: 1,
    size: 5,
    totalPages: null,
    sort: {value: "rank", direction: "asc"},
    date: {
      from: getDefaultDates().from,
      to: getDefaultDates().to,
    },
    errors: null,
    error: "",
    status: "",
  },
  errors: null,
  error: "",
  status: "",
};

const dashboardCallSlice = createSlice({
  name: "dashboard-call",
  initialState,
  reducers: {
    setChartsDate(state, action) {
      state.sessions.date = action.payload;
    },
    setMapChartsDate(state, action) {
      state.map.date = action.payload;
    },
    setUsersRankingDate(state, action) {
      state.usersRanking.date = action.payload;
    },
    setSessionsByMonth(state, action) {
      state.sessions.data.month = action.payload;
    },
    setSessionsByScore(state, action) {
      state.sessions.data.score = action.payload;
    },
    setServerStatus(state, action) {
      state.status = action.payload;
    },
    setPage(state, action) {
      state.usersRanking.page = Math.max(1, action.payload);
    },
    setSize(state, action) {
      state.usersRanking.size = action.payload;
    },
    setSort(state, action) {
      state.usersRanking.sort = action.payload;
    },
    clearCallDashboard() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSessionsByMonth.pending, (state) => {
        pendingState(state.sessions);
      })
      .addCase(getSessionsByMonth.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.sessions.data.month = formatSessionsByMonth(
            action.payload.data,
            state.sessions.date,
          );

          fulfilledState(state.sessions);
        } else {
          rejectedState(state.sessions, action);
        }
      })
      .addCase(getSessionsByMonth.rejected, (state, action) => {
        rejectedState(state.sessions, action);
      })

      .addCase(getSessionsByScore.pending, (state) => {
        pendingState(state.sessions);
      })
      .addCase(getSessionsByScore.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.sessions.data.score = formatSessionsByScore(
            action.payload.data,
          );

          fulfilledState(state.sessions);
        } else {
          rejectedState(state.sessions, action);
        }
      })
      .addCase(getSessionsByScore.rejected, (state, action) => {
        rejectedState(state.sessions, action);
      })

      .addCase(getSessionsByCountry.pending, (state) => {
        pendingState(state.map);
      })
      .addCase(getSessionsByCountry.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.map.data = formatSessionsByCountry(
            action.payload.data,
            action.payload.sessionScore,
          );

          fulfilledState(state.map);
        } else {
          rejectedState(state.map, action);
        }
      })
      .addCase(getSessionsByCountry.rejected, (state, action) => {
        rejectedState(state.map, action);
      })

      .addCase(getLastActivity.pending, (state) => {
        pendingState(state.lastActivity);
      })
      .addCase(getLastActivity.fulfilled, (state, action) => {
        state.lastActivity.data = action.payload.data;
        fulfilledState(state.lastActivity);
      })
      .addCase(getLastActivity.rejected, (state, action) => {
        rejectedState(state.lastActivity, action);
      })

      .addCase(getUsersRanking.pending, (state) => {
        pendingState(state.usersRanking);
      })
      .addCase(getUsersRanking.fulfilled, (state, action) => {
        state.usersRanking.data = action.payload.data;
        fulfilledState(state.usersRanking);
      })
      .addCase(getUsersRanking.rejected, (state, action) => {
        rejectedState(state.usersRanking, action);
      })

      .addCase(fetchAllDashboardData.pending, (state) => {
        pendingState(state);
        pendingState(state.sessions);
        pendingState(state.map);
        pendingState(state.lastActivity);
        pendingState(state.usersRanking);
      })
      .addCase(fetchAllDashboardData.fulfilled, (state, action) => {
        action.payload.forEach((res) => {
          switch (res.type) {
            case "sessionsByMonth":
              if (res.data) {
                state.sessions.data.month = formatSessionsByMonth(
                  res.data.data,
                  state.sessions.date,
                );
              } else {
                rejectedState(state.sessions, action);
              }
              break;
            case "sessionsByScore":
              if (res.data) {
                state.sessions.data.score = formatSessionsByScore(
                  res.data.data,
                );
              } else {
                rejectedState(state.sessions, action);
              }
              break;
            case "sessionsByCountry":
              if (res.data) {
                state.map.data = formatSessionsByCountry(
                  res.data.data,
                  res.data.sessionScore,
                );
              } else {
                rejectedState(state.map, action);
              }
              break;
            case "lastActivity":
              if (res.data) {
                state.lastActivity.data = res.data.data;
              } else {
                rejectedState(state.lastActivity, action);
              }
              break;
            case "usersRanking":
              if (res.data) {
                state.usersRanking.data = res.data.data;
              } else {
                rejectedState(state.usersRanking, action);
              }
              break;
            default:
              break;
          }
        });
        fulfilledState(state);
        fulfilledState(state.sessions);
        fulfilledState(state.map);
        fulfilledState(state.lastActivity);
        fulfilledState(state.usersRanking);
      })
      .addCase(fetchAllDashboardData.rejected, (state, action) => {
        rejectedState(state, action);
        rejectedState(state.sessions, action);
        rejectedState(state.map, action);
        rejectedState(state.lastActivity, action);
        rejectedState(state.usersRanking, action);
      })

      .addCase(fetchSessionsChartsData.pending, (state) => {
        pendingState(state.sessions);
      })
      .addCase(fetchSessionsChartsData.fulfilled, (state, action) => {
        action.payload.forEach((res) => {
          switch (res.type) {
            case "sessionsByMonth":
              if (res.data) {
                state.sessions.data.month = formatSessionsByMonth(
                  res.data.data,
                  state.sessions.date,
                );
              } else {
                rejectedState(state.sessions, action);
              }
              break;
            case "sessionsByScore":
              if (res.data) {
                state.sessions.data.score = formatSessionsByScore(
                  res.data.data,
                );
              } else {
                rejectedState(state.sessions, action);
              }
              break;
            default:
              break;
          }
        });
        fulfilledState(state.sessions);
      })
      .addCase(fetchSessionsChartsData.rejected, (state, action) => {
        rejectedState(state.sessions, action);
      });
  },
});

export const {
  setChartsDate,
  setMapChartsDate,
  setUsersRankingDate,
  setSessionsByMonth,
  setSessionsByScore,
  setServerStatus,
  clearCallDashboard,
  setPage,
  setSize,
  setSort,
} = dashboardCallSlice.actions;

export default dashboardCallSlice.reducer;
